import { useEffect, useContext, useState } from "react"
import { useIntl } from "react-intl"
import { differenceInMonths, formatDistanceToNow, fromUnixTime } from "date-fns"
import { Link } from "react-router"
import { css } from "@emotion/react"

import useRawState from "../../hooks/useRawState"
import useBridge from "../../hooks/useBridge"

import ViewPortContext from "../../context/ViewPort"
import LocaleContext from "../../context/Locale"

import LazyLoader from "../_general/LazyLoader"
import Loader from "../_general/Loader"
import Table from "../_general/Table"
import PageSubtitle from "../_general/PageSubtitle"
import IndexGauge from "./IndexGauge"
import PitchTypeIcon from "../pitch/PitchTypeIcon"
import BootIndexIcon from "../analysis/BootIndexIcon"
import IndexResultsModal from "./IndexResultsModal"
import BootAdviceModal from "./BootAdviceModal"
import { backendColors, colors, fonts, getBestTrainingGoalFromIndexResults, isIndexResultValid } from "shared-ui"
import { transparentize } from "polished"
import ScoreIndicator from "../_general/ScoreIndicator"

export default function PerformancePitchesTable({
    venue, venueIndexes, pitches, selectedTrainingGoal,
    selectedIndexLabel, upliftIndexData, indexCategories,
    stickyHeader = false, headerStyle, referenceDate = new Date(),
    expiredVisible,
}) {

    const { formatMessage } = useIntl()

    const thisVenuesIndexes = venueIndexes[venue?.id] || []

    const { data: bootTypes, loading: loadingBootTypes } = useBridge('/api/current/frontend-bridge/boot-types')
    const { datefnsLocale } = useContext(LocaleContext)

    const getEnrichedRowData = (pitch) => {

        if (loadingBootTypes) return {}

        const thisPitchIndexes = thisVenuesIndexes
            .filter(ind => ind.pitchId === pitch.id)
            .map(ind => ({ ...ind, _isExpired_: !isIndexResultValid(ind, referenceDate) }))

        if (thisPitchIndexes.length === 0) return {}

        //gauges to show
        const gaugesIndexData = thisPitchIndexes
            .filter(ind => ind.indexCategory.indexCategoryGroup === 'performance')
            .toSorted((a, b) => (a.indexCategory.sortRank ?? 0) < (b.indexCategory.sortRank ?? 0) ? -1 : 1)

        // boot
        const bootIndex = thisPitchIndexes?.find(ind => ind.indexCategory.indexCategoryGroup === 'boot')
        const recommendedBootType = bootIndex?.bootRecommendations?.[0]?.bootType

        // best suited for
        const bestTrainingGoal = getBestTrainingGoalFromIndexResults(thisPitchIndexes, referenceDate)

        // last analysis (of indexresults)
        const latest = thisPitchIndexes.reduce((a, b) => (a.latestTestTimestamp > b.latestTestTimestamp) ? a : b)
        const latestTimestamp = latest.latestTestTimestamp
        if (!latestTimestamp) return {}
        const distance = formatDistanceToNow(fromUnixTime(latestTimestamp), { addSuffix: true, locale: datefnsLocale })
        const alertLatestAnalysisDate = differenceInMonths(new Date(), fromUnixTime(latestTimestamp)) >= 1

        return {
            gaugesIndexData,
            bootType: recommendedBootType,
            bootName: bootTypes?.[recommendedBootType],
            bootIndexExpired: bootIndex?._isExpired_,
            bestSuitedIndex: bestTrainingGoal,
            alertLatestAnalysisDate,
            formattedDistance: distance,
            allIndexData: thisPitchIndexes,
            lastAnalTimestamp: latestTimestamp,
        }
    }

    let impactTitle, energyTitle, gripTitle

    if (thisVenuesIndexes.length > 0) {

        impactTitle = (indexCategories?.find(cat => cat.indexCategoryType === 'impact')?.name);
        energyTitle = (indexCategories?.find(cat => cat.indexCategoryType === 'energy')?.name);
        gripTitle = (indexCategories?.find(cat => cat.indexCategoryType === 'traction')?.name);
    }

    const [trainingGoalModalOpenData, setTrainingGoalModalOpenData] = useState(null)
    const [bootModalOpenData, setBootModalOpenData] = useState(null)

    const { showCompactUI } = useContext(ViewPortContext)

    return <>
        <Table
            data={pitches}
            rowKey={pitch => pitch.id}
            headerBg={colors.dark}
            stickyHeader={stickyHeader}
            colgroup={<colgroup>
                <col key="venuePitches" css={showCompactUI ? css`width: 100%;` : css`width: 24em;`} />
                {selectedTrainingGoal && <col key="selectedTrainingGoal" css={css`width: 1px;`} />}
                <col key="bestSuitedFor" />
                <col key="bootAdvice" css={css`width: 26em;`} />
                {!showCompactUI &&
                    <>
                        <col key="impact" css={css`width: 8.5em;`} />
                        <col key="energy" css={css`width: 8.5em;`} />
                        <col key="grip" css={css`width: 8.5em;`} />
                    </>
                }
                {showCompactUI && <col key="combinedGauges" css={css`width: 1px;`} />}
                <col key="lastTested" css={css`width: 14em;`} />
            </colgroup>
            }
            headerStyle={headerStyle}
            columns={[
                {
                    key: 'venuePitches',
                    header: <PageSubtitle css={style.venueHeader}>
                        {venue?.name}
                    </PageSubtitle>,
                    shrink: false,
                    sortable: false,
                    wide: false,
                    renderData: (pitch) => <div css={style.responsiveContent}>
                        <Link to={`/pitch/${pitch.id}/index`} css={style.pitchLink}>
                            <PitchTypeIcon pitch={pitch} css={style.pitchTypeIcon} />
                            <PageSubtitle css={style.pitchName}>
                                {pitch.name}
                            </PageSubtitle>
                        </Link>

                        <LazyLoader>
                            <div css={style.loaderSpinner}>
                                <LastIndicesFetcher pitch={pitch} upliftIndexData={upliftIndexData} />
                            </div>
                        </LazyLoader>
                    </div>
                },
                {
                    key: 'selectedTrainingGoal',
                    header: <div css={css`color: ${colors.main1}; font-weight: 500;`}>
                        {selectedTrainingGoal ? selectedIndexLabel : ''}
                    </div>,
                    shrink: true,
                    sortable: false,
                    wide: false,
                    renderData: (pitch) => {
                        const { allIndexData } = getEnrichedRowData(pitch)
                        const msgNotApplicableAbbr = formatMessage({ id: 'notApplicableAbbr' })

                        if (!allIndexData) return <div css={[style.suitabilityHighlight, css`filter: grayscale(1); opacity: 0.5;`]}>
                            <ScoreIndicator isNull={true} notApplicableAbbrString={msgNotApplicableAbbr} />
                        </div>

                        const selectedIndexData = allIndexData.find(ind => ind.indexCategoryId === selectedTrainingGoal)
                        const isNull = selectedIndexData?.value === null || selectedIndexData === undefined
                        const selectedIndexVal = Math.round(selectedIndexData?.value * 10) / 10
                        const selectedIndexAssesmentColor = backendColors[selectedIndexData?.score?.assessment?.frontendCode] || colors.soft
                        const showExpiredAsNull = selectedIndexData?._isExpired_ && !expiredVisible

                        return <div css={[
                            style.suitabilityHighlight,
                            isNull || selectedIndexData?._isExpired_ ? css`filter: grayscale(1); opacity: 0.5;` : null
                        ]}>
                            <ScoreIndicator
                                isNull={showExpiredAsNull || isNull}
                                color={selectedIndexAssesmentColor}
                                valueString={selectedIndexVal?.toString()}
                                unitName={selectedIndexLabel}
                                notApplicableAbbrString={msgNotApplicableAbbr}
                                css={css`font-family: ${fonts.special};`}
                            />
                        </div>
                    }
                },
                {
                    key: 'bestSuitedFor',
                    header: formatMessage({ id: 'bestSuitedFor' }),
                    shrink: false,
                    sortable: false,
                    wide: false,
                    renderData: (pitch) => {
                        const { bestSuitedIndex, allIndexData } = getEnrichedRowData(pitch)
                        if (!bestSuitedIndex) return null
                        return <div css={style.responsiveContent}>
                            <div
                                css={[
                                    style.clickableText,
                                    bestSuitedIndex._isExpired_ ? style.isExpired(expiredVisible) : null
                                ]}
                                onClick={() => { setTrainingGoalModalOpenData({ pitch, indexData: allIndexData }) }}
                            >
                                {bestSuitedIndex?.indexCategory?.name ?? formatMessage({ id: 'notApplicableAbbr' })}
                            </div>
                        </div>
                    }
                },
                {
                    key: 'bootAdvice',
                    header: formatMessage({ id: 'wear' }),
                    shrink: false,
                    sortable: false,
                    wide: false,
                    renderData: (pitch) => {
                        const { bootName, bootType, allIndexData, bootIndexExpired } = getEnrichedRowData(pitch)
                        if (!bootType || !bootName) return null

                        return <div
                            css={[
                                style.bootContainer,
                                style.clickableText,
                                bootIndexExpired ? style.isExpired(expiredVisible) : null
                            ]}
                            onClick={() => { setBootModalOpenData({ pitch, indexData: allIndexData }) }}
                        >
                            <div css={style.responsiveContent}>
                                <BootIndexIcon
                                    bootBackendCode={bootType}
                                    bootNameLocalised={bootName}
                                    hoverOverlayText={false}
                                    style={{ minWidth: '1.3em', width: '1.3em' }}
                                />
                            </div>
                            <div css={style.bootName}>
                                {bootName ?? formatMessage({ id: 'notApplicableAbbr' })}
                            </div>
                        </div>
                    }
                },
                {
                    key: 'impact',
                    header: impactTitle || '',
                    shrink: true,
                    sortable: false,
                    wide: false,
                    renderData: (pitch) => {
                        const { gaugesIndexData } = getEnrichedRowData(pitch)
                        if (!gaugesIndexData) return null
                        const ind = gaugesIndexData.find(ind => ind.indexCategory.indexCategoryType === 'impact')
                        if (!ind) return null

                        return <div css={[style.responsiveContent, style.gaugeSize, ind._isExpired_ ? style.isExpired(expiredVisible) : null]}>
                            <IndexGauge
                                showName={false}
                                indexData={{
                                    localisedName: ind.indexCategory.name,
                                    indexValue: ind.value,
                                    frontendCode: ind.score?.assessment?.frontendCode,
                                    indexType: ind.indexCategory.indexCategoryGroup,
                                }}
                            />
                        </div>
                    }
                },
                {
                    key: 'energy',
                    header: energyTitle || '',
                    shrink: true,
                    sortable: false,
                    wide: false,
                    renderData: (pitch) => {
                        const { gaugesIndexData } = getEnrichedRowData(pitch)
                        if (!gaugesIndexData) return null
                        const ind = gaugesIndexData.find(ind => ind.indexCategory.indexCategoryType === 'energy')
                        if (!ind) return null

                        return <div css={[style.responsiveContent, style.gaugeSize, ind._isExpired_ ? style.isExpired(expiredVisible) : null]}>
                            <IndexGauge
                                showName={false}
                                indexData={{
                                    localisedName: ind.indexCategory.name,
                                    indexValue: ind.value,
                                    frontendCode: ind.score?.assessment?.frontendCode,
                                    indexType: ind.indexCategory.indexCategoryGroup,
                                }}
                            />
                        </div>
                    }
                },
                {
                    key: 'grip',
                    header: gripTitle || '',
                    shrink: true,
                    sortable: false,
                    wide: false,
                    renderData: (pitch) => {
                        const { gaugesIndexData } = getEnrichedRowData(pitch)
                        if (!gaugesIndexData) return null
                        const ind = gaugesIndexData.find(ind => ind.indexCategory.indexCategoryType === 'traction')
                        if (!ind) return null

                        return <div css={[style.responsiveContent, style.gaugeSize, ind._isExpired_ ? style.isExpired(expiredVisible) : null]}>
                            <IndexGauge
                                showName={false}
                                indexData={{
                                    localisedName: ind.indexCategory.name,
                                    indexValue: ind.value,
                                    frontendCode: ind.score?.assessment?.frontendCode,
                                    indexType: ind.indexCategory.indexCategoryGroup,
                                }}
                            />
                        </div>
                    }
                },
                {
                    key: 'combinedGauges',
                    header: `${impactTitle || ''} / ${energyTitle || ''} / ${gripTitle || ''}`,
                    shrink: true,
                    sortable: false,
                    wide: false,
                    renderData: (pitch) => {
                        const { gaugesIndexData } = getEnrichedRowData(pitch)
                        if (!gaugesIndexData) return null
                        return <div css={[style.responsiveContent, css`display: flex; gap: 0 0.5em;`]}>
                            {gaugesIndexData.map(ind => {
                                return <div
                                    key={`${pitch.id}-${ind.indexCategory.id}`}
                                    css={[style.gaugeSize, ind._isExpired_ ? style.isExpired(expiredVisible) : null]}
                                >
                                    <IndexGauge
                                        showName={false}
                                        indexData={{
                                            localisedName: ind.indexCategory.name,
                                            indexValue: ind.value,
                                            frontendCode: ind.score?.assessment?.frontendCode,
                                            indexType: ind.indexCategory.indexCategoryGroup,
                                        }}
                                    />
                                </div>
                            })
                            }
                        </div>
                    }
                },
                {
                    key: 'lastTested',
                    header: formatMessage({ id: 'lastAnalysis' }),
                    shrink: false,
                    sortable: false,
                    wide: false,
                    renderData: (pitch) => {
                        const { gaugesIndexData, alertLatestAnalysisDate, formattedDistance, lastAnalTimestamp } = getEnrichedRowData(pitch)
                        // check to see if we have any index data at all
                        if (!gaugesIndexData) return null
                        let msg = formatMessage({ id: 'never' })
                        if (formattedDistance) msg = formattedDistance

                        return <Link
                            to={`/pitch/${pitch.id}/index?timestamp=${lastAnalTimestamp}`}
                            css={css`
                                color: ${alertLatestAnalysisDate ? colors.warn : 'inherit'};
                                &:hover {
                                    text-decoration: ${gaugesIndexData ? 'underline' : 'none'};
                                }
                            `}
                        >
                            {msg}
                        </Link>
                    }
                },
            ].filter(col => [
                "venuePitches",
                selectedTrainingGoal ? "selectedTrainingGoal" : null,
                showCompactUI ? null : "bestSuitedFor",
                "bootAdvice",
                showCompactUI ? null : "impact",
                showCompactUI ? null : "energy",
                showCompactUI ? null : "grip",
                showCompactUI ? "combinedGauges" : null,
                showCompactUI ? null : "lastTested"
            ].includes(col.key))}
        />
        {trainingGoalModalOpenData &&
            <IndexResultsModal
                pitch={trainingGoalModalOpenData?.pitch}
                indexData={trainingGoalModalOpenData?.indexData}
                onClickClose={() => setTrainingGoalModalOpenData(null)}
                referenceTimestamp={referenceDate.valueOf()}
                showExpired={expiredVisible}
            />
        }
        {bootModalOpenData && bootTypes &&
            <BootAdviceModal
                pitch={bootModalOpenData?.pitch}
                indexData={bootModalOpenData?.indexData}
                bootTypes={bootTypes}
                onClickClose={() => setBootModalOpenData(null)}
            />
        }
    </>
}

function LastIndicesFetcher({ pitch, upliftIndexData }) {

    const { data: latestIndices, isBusy: loadingLatestIndices } = useRawState(`/api/current/pitches/${pitch.id}/index-results/latest`)

    useEffect(() => {
        if (!!latestIndices && pitch) {
            const venueId = Number(pitch.venue?.split('/').pop() ?? '')
            for (const ind of latestIndices) {
                upliftIndexData(venueId, pitch.id, ind.indexCategory.id, ind)
            }
        }
    }, [latestIndices])

    return loadingLatestIndices ? <Loader size='1.5em' /> : null
}

const style = {
    pitchLink: css`
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0 1em;
        cursor: pointer;     
        &:hover{ 
            > * {
                color: ${colors.main1};
                text-decoration: underline; 
                text-decoration-color: rgba(255,255,255,0.5); 
            }
        };                   
        @media screen and (max-width: 600px) {
            text-decoration: underline; 
            text-decoration-color: rgba(255,255,255,0.5);                        
        }   
    `,
    clickableText: css`
        color: white;
        cursor: pointer;
        font-family: ${fonts.special};
        font-size: 1.5em;

        &:hover {
            color: ${colors.main1};
        }
    `,
    suitabilityHighlight: css`
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: center;

        border-radius: 5px;
        padding: 0.75em 1em;
        background: ${transparentize(0.7, colors.main1)};

        border: 2px dashed ${transparentize(0.7, colors.main1)};
    `,
    responsiveContent: css`
        @media screen and (max-width: 800px) {
            font-size: 0.8em;
        }
    `,
    pitchTypeIcon: css`
        min-width: 3em; 
        max-width: 3em;
        @media screen and (max-width: 800px) {
            display: none;
    `,
    venueHeader: css`
        color: white;
        margin: 0;
        font-size: 1.6em;
    `,
    pitchName: css`
        color: white;
        margin: 0;
        font-size: 1.5em;
    `,
    loaderSpinner: css`
        position: absolute; 
        right: 1em; 
        top: 50%; 
        transform: translateY(-50%);
    `,
    bootContainer: css`
        display: flex;
        align-items: center;
        gap: 1em;
        &:hover {
            * {
                color: ${colors.main1}};
            }
        }
    `,
    bootName: css`@media screen and (max-width: 800px) { display: none; }`,
    gaugeSize: css`font-size: 1.2em;`,
    isExpired: (showExpired) => css`
        ${!showExpired && 'display: none;'}
        filter: grayscale(1); 
        opacity: 0.5;
        &:hover {
            filter: unset;
            opacity: 1;
        }
    `
}